/**
 * 의존성 없는 최소 XLSX 작성기 (무압축 ZIP + SpreadsheetML).
 * 헤더 굵게, 숫자는 숫자 셀, 나머지는 인라인 문자열. 대량 행에서도 문자열 결합만 한다.
 */
import { compileFormula, FormulaError } from './formula.js';
const enc = new TextEncoder();
export function buildXlsx(sheet) {
    const name = (sheet.name || 'Sheet1').replace(/[\\/?*[\]:]/g, ' ').slice(0, 31);
    const hr = [...(sheet.headerRows ?? []), sheet.headers];
    const nh = hr.length, ncol = Math.max(1, sheet.headers.length);
    const cols = sheet.widths?.length
        ? `<cols>${sheet.widths.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${Math.max(4, Math.min(80, w))}" customWidth="1"/>`).join('')}</cols>`
        : '';
    const { xml: stylesXml, xf } = stylesheet(sheet.styles ?? []);
    const fr = sheet.freeze?.rows ?? nh, fc = sheet.freeze?.cols ?? 0;
    const pane = fr || fc
        ? `<pane${fc ? ` xSplit="${fc}"` : ''}${fr ? ` ySplit="${fr}"` : ''} topLeftCell="${colName(fc)}${fr + 1}" activePane="${fr && fc ? 'bottomRight' : fr ? 'bottomLeft' : 'topRight'}" state="frozen"/>`
        : '';
    const maxOutline = Math.max(0, ...(sheet.outline ?? []).map(x => x ?? 0));
    const parts = [];
    parts.push(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">${maxOutline ? '<sheetPr><outlinePr summaryBelow="1"/></sheetPr>' : ''}<sheetViews><sheetView workbookViewId="0">${pane}</sheetView></sheetViews><sheetFormatPr defaultRowHeight="15"${maxOutline ? ` outlineLevelRow="${maxOutline}"` : ''}/>${cols}<sheetData>`);
    for (let i = 0; i < nh; i++)
        parts.push(rowXml(i + 1, hr[i], () => 1, 0));
    const cs = sheet.colStyles, rs = sheet.rowStyles, cst = sheet.cellStyles;
    for (let i = 0; i < sheet.rows.length; i++) {
        const rowSt = rs?.[i], cellSt = cst?.[i];
        const st = (c) => { const k = cellSt?.[c] ?? rowSt ?? cs?.[c]; return k == null ? 0 : xf(k); };
        parts.push(rowXml(i + nh + 1, sheet.rows[i], st, sheet.outline?.[i] ?? 0));
    }
    parts.push('</sheetData>');
    const last = sheet.rows.length + nh;
    if (sheet.autoFilter !== false)
        parts.push(`<autoFilter ref="A${nh}:${colName(ncol - 1)}${last}"/>`);
    if (sheet.merges?.length)
        parts.push(`<mergeCells count="${sheet.merges.length}">${sheet.merges.map(m => `<mergeCell ref="${m}"/>`).join('')}</mergeCells>`);
    const notes = (sheet.notes ?? []).filter(n => n && n.text && n.row >= 0 && n.col >= 0);
    if (notes.length)
        parts.push('<legacyDrawing r:id="rId2"/>');
    parts.push('</worksheet>');
    const files = [
        ['[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${notes.length ? '<Default Extension="vml" ContentType="application/vnd.openxmlformats-officedocument.vmlDrawing"/><Override PartName="/xl/comments1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.comments+xml"/>' : ''}</Types>`],
        ['_rels/.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`],
        ['xl/workbook.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${esc(name)}" sheetId="1" r:id="rId1"/></sheets>${sheet.autoFilter !== false ? `<definedNames><definedName name="_xlnm._FilterDatabase" localSheetId="0" hidden="1">'${esc(name)}'!$A$${nh}:$${colName(ncol - 1)}$${last}</definedName></definedNames>` : ''}</workbook>`],
        ['xl/_rels/workbook.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`],
        ['xl/styles.xml', stylesXml],
        ['xl/worksheets/sheet1.xml', parts.join('')],
    ];
    if (notes.length)
        files.push(...commentParts(notes, nh));
    return zipStore(files.map(([n, s]) => [n, enc.encode(s)]));
}
/** 메모 → comments1.xml + VML(옛 방식 도형, 엑셀이 메모 상자를 그릴 때 필요) + 시트 rels */
function commentParts(notes, nh) {
    const authors = [];
    const aid = (a) => { const k = a || 'OneGrid'; let i = authors.indexOf(k); if (i < 0) {
        authors.push(k);
        i = authors.length - 1;
    } return i; };
    const list = notes.map(n => ({ r: n.row + nh, c: n.col, ref: colName(n.col) + (n.row + nh + 1), text: n.text, a: aid(n.author), by: n.author }));
    const comments = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><comments xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><authors>${authors.map(a => `<author>${esc(a)}</author>`).join('')}</authors><commentList>${list.map(n => `<comment ref="${n.ref}" authorId="${n.a}"><text>${n.by ? `<r><rPr><b/><sz val="9"/><rFont val="맑은 고딕"/></rPr><t xml:space="preserve">${esc(authors[n.a])}:
</t></r>` : ''}<r><rPr><sz val="9"/><rFont val="맑은 고딕"/></rPr><t xml:space="preserve">${esc(n.text)}</t></r></text></comment>`).join('')}</commentList></comments>`;
    const vml = `<xml xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><o:shapelayout v:ext="edit"><o:idmap v:ext="edit" data="1"/></o:shapelayout><v:shapetype id="_x0000_t202" coordsize="21600,21600" o:spt="202" path="m,l,21600r21600,l21600,xe"><v:stroke joinstyle="miter"/><v:path gradientshapeok="t" o:connecttype="rect"/></v:shapetype>${list.map((n, i) => `<v:shape id="_x0000_s${1025 + i}" type="#_x0000_t202" style="position:absolute;margin-left:80pt;margin-top:2pt;width:144pt;height:60pt;z-index:${i + 1};visibility:hidden" fillcolor="#ffffe1" o:insetmode="auto"><v:fill color2="#ffffe1"/><v:shadow on="t" color="black" obscured="t"/><v:path o:connecttype="none"/><v:textbox style="mso-direction-alt:auto"><div style="text-align:left"></div></v:textbox><x:ClientData ObjectType="Note"><x:MoveWithCells/><x:SizeWithCells/><x:Anchor>${n.c + 1}, 15, ${n.r}, 2, ${n.c + 3}, 15, ${n.r + 4}, 4</x:Anchor><x:AutoFill>False</x:AutoFill><x:Row>${n.r}</x:Row><x:Column>${n.c}</x:Column></x:ClientData></v:shape>`).join('')}</xml>`;
    const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/comments" Target="../comments1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/vmlDrawing" Target="../drawings/vmlDrawing1.vml"/></Relationships>`;
    return [['xl/comments1.xml', comments], ['xl/drawings/vmlDrawing1.vml', vml], ['xl/worksheets/_rels/sheet1.xml.rels', rels]];
}
/** styles.xml: xf 0 기본, 1 헤더(굵게·배경·테두리·가운데), 2.. 사용자 서식 */
function stylesheet(styles) {
    const fonts = ['<font><sz val="10"/><name val="맑은 고딕"/></font>', '<font><b/><sz val="10"/><name val="맑은 고딕"/></font>'];
    const fills = ['<fill><patternFill patternType="none"/></fill>', '<fill><patternFill patternType="gray125"/></fill>', '<fill><patternFill patternType="solid"><fgColor rgb="FFE8EEF4"/></patternFill></fill>'];
    const borders = ['<border/>', '<border><left style="thin"><color rgb="FFB7C3CF"/></left><right style="thin"><color rgb="FFB7C3CF"/></right><top style="thin"><color rgb="FFB7C3CF"/></top><bottom style="thin"><color rgb="FFB7C3CF"/></bottom></border>'];
    const fmts = [];
    const idx = (arr, x) => { let i = arr.indexOf(x); if (i < 0) {
        arr.push(x);
        i = arr.length - 1;
    } return i; };
    const xfs = ['<xf fontId="0" fillId="0" borderId="0" xfId="0"/>', '<xf fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>'];
    for (const st of styles) {
        const font = idx(fonts, `<font>${st.bold ? '<b/>' : ''}${st.italic ? '<i/>' : ''}<sz val="10"/>${st.color ? `<color rgb="FF${st.color.replace('#', '').toUpperCase()}"/>` : ''}<name val="맑은 고딕"/></font>`);
        const fill = st.fill ? idx(fills, `<fill><patternFill patternType="solid"><fgColor rgb="FF${st.fill.replace('#', '').toUpperCase()}"/></patternFill></fill>`) : 0;
        const fmt = st.numFmt ? 164 + idx(fmts, st.numFmt) : 0;
        const al = st.indent || st.align ? `<alignment${st.align ? ` horizontal="${st.align}"` : st.indent ? ' horizontal="left"' : ''}${st.indent ? ` indent="${st.indent}"` : ''}/>` : '';
        xfs.push(`<xf numFmtId="${fmt}" fontId="${font}" fillId="${fill}" borderId="${st.border ? 1 : 0}" xfId="0"${fmt ? ' applyNumberFormat="1"' : ''}${font ? ' applyFont="1"' : ''}${fill ? ' applyFill="1"' : ''}${st.border ? ' applyBorder="1"' : ''}${al ? ' applyAlignment="1">' + al + '</xf>' : '/>'}`);
    }
    const xml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">${fmts.length ? `<numFmts count="${fmts.length}">${fmts.map((f, i) => `<numFmt numFmtId="${164 + i}" formatCode="${esc(f)}"/>`).join('')}</numFmts>` : ''}<fonts count="${fonts.length}">${fonts.join('')}</fonts><fills count="${fills.length}">${fills.join('')}</fills><borders count="${borders.length}">${borders.join('')}</borders><cellStyleXfs count="1"><xf/></cellStyleXfs><cellXfs count="${xfs.length}">${xfs.join('')}</cellXfs></styleSheet>`;
    return { xml, xf: i => (i >= 0 && i < styles.length ? i + 2 : 0) };
}
function rowXml(r, cells, style, outline) {
    let s = `<row r="${r}"${outline ? ` outlineLevel="${outline}"` : ''}>`;
    for (let c = 0; c < cells.length; c++) {
        const v = cells[c];
        const sv = style(c);
        const ref = colName(c) + r;
        const st = sv ? ` s="${sv}"` : '';
        if (v == null || v === '') {
            if (sv)
                s += `<c r="${ref}"${st}/>`;
            continue;
        }
        if (typeof v === 'number' && isFinite(v))
            s += `<c r="${ref}"${st}><v>${v}</v></c>`;
        else if (typeof v === 'boolean')
            s += `<c r="${ref}"${st} t="b"><v>${v ? 1 : 0}</v></c>`;
        else
            s += `<c r="${ref}"${st} t="inlineStr"><is><t xml:space="preserve">${esc(String(v))}</t></is></c>`;
    }
    return s + '</row>';
}
export { colName, excelDate } from './xlsx-core.js';
import { colName } from './xlsx-core.js';
function esc(s) {
    // XML 1.0 에서 허용되지 않는 제어문자 제거
    return s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
// ── 무압축 ZIP ──
const CRC = (() => { const t = new Uint32Array(256); for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++)
        c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
} return t; })();
function crc32(b) { let c = 0xffffffff; for (let i = 0; i < b.length; i++)
    c = CRC[(c ^ b[i]) & 0xff] ^ (c >>> 8); return (c ^ 0xffffffff) >>> 0; }
export function zipStore(files) {
    const chunks = [];
    const central = [];
    let offset = 0;
    for (const [name, data] of files) {
        const nb = enc.encode(name);
        const crc = crc32(data);
        const h = new DataView(new ArrayBuffer(30));
        h.setUint32(0, 0x04034b50, true);
        h.setUint16(4, 20, true);
        h.setUint16(6, 0x0800, true);
        h.setUint16(8, 0, true);
        h.setUint32(14, crc, true);
        h.setUint32(18, data.length, true);
        h.setUint32(22, data.length, true);
        h.setUint16(26, nb.length, true);
        chunks.push(new Uint8Array(h.buffer), nb, data);
        const c = new DataView(new ArrayBuffer(46));
        c.setUint32(0, 0x02014b50, true);
        c.setUint16(4, 20, true);
        c.setUint16(6, 20, true);
        c.setUint16(8, 0x0800, true);
        c.setUint32(16, crc, true);
        c.setUint32(20, data.length, true);
        c.setUint32(24, data.length, true);
        c.setUint16(28, nb.length, true);
        c.setUint32(42, offset, true);
        central.push(new Uint8Array(c.buffer), nb);
        offset += 30 + nb.length + data.length;
    }
    const cdSize = central.reduce((a, b) => a + b.length, 0);
    const e = new DataView(new ArrayBuffer(22));
    e.setUint32(0, 0x06054b50, true);
    e.setUint16(8, files.length, true);
    e.setUint16(10, files.length, true);
    e.setUint32(12, cdSize, true);
    e.setUint32(16, offset, true);
    const all = [...chunks, ...central, new Uint8Array(e.buffer)];
    const out = new Uint8Array(all.reduce((a, b) => a + b.length, 0));
    let p = 0;
    for (const a of all) {
        out.set(a, p);
        p += a.length;
    }
    return out;
}
/** ZIP 항목 풀기 (무압축·deflate). deflate 는 DecompressionStream('deflate-raw'). want 로 고른 항목만 */
async function unzip(buf, want) {
    const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    let eocd = -1;
    for (let i = buf.length - 22; i >= Math.max(0, buf.length - 65557); i--)
        if (dv.getUint32(i, true) === 0x06054b50) {
            eocd = i;
            break;
        }
    if (eocd < 0)
        throw new Error('xlsx(zip) 형식이 아닙니다');
    const n = dv.getUint16(eocd + 10, true);
    let p = dv.getUint32(eocd + 16, true);
    const out = new Map();
    const dec = new TextDecoder();
    for (let k = 0; k < n; k++) {
        if (dv.getUint32(p, true) !== 0x02014b50)
            break;
        const method = dv.getUint16(p + 10, true), csize = dv.getUint32(p + 20, true);
        const nlen = dv.getUint16(p + 28, true), xlen = dv.getUint16(p + 30, true), clen = dv.getUint16(p + 32, true), lho = dv.getUint32(p + 42, true);
        const name = dec.decode(buf.subarray(p + 46, p + 46 + nlen));
        p += 46 + nlen + xlen + clen;
        if (!want(name))
            continue;
        const start = lho + 30 + dv.getUint16(lho + 26, true) + dv.getUint16(lho + 28, true);
        const data = buf.subarray(start, start + csize);
        if (method === 0)
            out.set(name, data);
        else if (method === 8) {
            const ds = new globalThis.DecompressionStream('deflate-raw');
            const ab = await new Response(new Blob([data]).stream().pipeThrough(ds)).arrayBuffer();
            out.set(name, new Uint8Array(ab));
        }
        else
            throw new Error('지원하지 않는 압축 방식: ' + method);
    }
    return out;
}
const unxml = (s) => s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCodePoint(parseInt(h, 16))).replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(+d)).replace(/&amp;/g, '&');
function texts(xml) { let t = ''; for (const m of xml.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g))
    t += m[1]; return unxml(t); }
function colIndex(ref) { let n = 0; for (const ch of ref.replace(/[\d$]+$/, '').replace(/\$/g, ''))
    n = n * 26 + ch.charCodeAt(0) - 64; return n - 1; }
const attr = (s, k) => new RegExp(`(?:^|\\s)${k}="([^"]*)"`).exec(s)?.[1];
/** 내장 날짜·시간 서식 번호 (14-22, 45-47 + 동아시아 로캘 27-36, 50-58) */
const BUILTIN_DATE = { 14: 'date', 15: 'date', 16: 'date', 17: 'date', 18: 'time', 19: 'time', 20: 'time', 21: 'time', 22: 'datetime', 45: 'time', 46: 'time', 47: 'time' };
for (let i = 27; i <= 36; i++)
    BUILTIN_DATE[i] = 'date';
for (let i = 50; i <= 58; i++)
    BUILTIN_DATE[i] = 'date';
/**
 * 숫자 서식이 날짜·시간인가 (순수). id 가 내장 날짜 번호면 그것, 아니면 서식 문자열을 본다:
 * 따옴표 글자·\x·[색]·[$-412] 를 빼고 y/d(·시간 아닌 m) 가 있으면 날짜, h/s 가 있으면 시간
 */
export function dateFormatKind(code, id) {
    if (id != null && BUILTIN_DATE[id])
        return BUILTIN_DATE[id];
    if (!code)
        return null;
    let s = code.split(';')[0];
    s = s.replace(/"[^"]*"/g, '').replace(/\\./g, '').replace(/[_*]./g, '');
    s = s.replace(/\[(h+|m+|s+)\]/gi, '$1').replace(/\[[^\]]*\]/g, '').replace(/AM\/PM|A\/P|上午\/下午/gi, '');
    if (/^general$/i.test(s.trim()))
        return null;
    const time = /[hs]/i.test(s);
    const date = /[yd]/i.test(s) || (/m/i.test(s) && !time);
    return date && time ? 'datetime' : date ? 'date' : time ? 'time' : null;
}
/** 엑셀 일련번호 → 날짜·시간 글자 (kind 에 맞춰) */
export function excelDateTime(serial, kind, seconds = false) {
    const ms = Math.round((serial - 25569) * 86400) * 1000;
    const iso = new Date(ms).toISOString();
    const tm = iso.slice(11, seconds ? 19 : 16);
    return kind === 'date' ? iso.slice(0, 10) : kind === 'time' ? tm : `${iso.slice(0, 10)} ${tm}`;
}
/** 공유 수식을 다른 셀 위치로 옮김 ($ 고정 참조는 그대로, 따옴표 안 글자는 건드리지 않음) */
export function shiftFormula(f, dr, dc) {
    if (!dr && !dc)
        return f;
    return f.split(/("[^"]*")/).map((part, i) => i % 2 ? part : part.replace(/(?<![A-Za-z_\d.!])(\$?)([A-Z]{1,3})(\$?)(\d+)(?![\d(A-Za-z_])/g, (_, a, c, b, r) => `${a}${a ? c : colName(Math.max(0, colIndex(c) + dc))}${b}${b ? r : Math.max(1, +r + dr)}`)).join('');
}
/**
 * 엑셀 수식을 내장 수식 엔진(formula.ts)으로 계산 (순수). get(셀 주소) 로 다른 셀 값을 읽는다.
 * 같은 시트 참조(A1, $A$1, A1:B3 범위)만 — 다른 시트·이름 정의·배열 수식은 FormulaError
 */
export function evalExcelFormula(f, get) {
    let src = f.replace(/^=/, '');
    if (/!/.test(src.replace(/"[^"]*"/g, '')))
        throw new FormulaError('다른 시트 참조는 계산하지 않습니다');
    src = src.split(/("[^"]*")/).map((part, i) => {
        if (i % 2)
            return "'" + part.slice(1, -1).replace(/'/g, "\\'") + "'";
        return part.replace(/\$/g, '')
            .replace(/\b([A-Z]{1,3})(\d+):([A-Z]{1,3})(\d+)\b/g, (_, c1, r1, c2, r2) => {
            const ca = colIndex(c1), cb = colIndex(c2), ra = +r1, rb = +r2, out = [];
            if ((Math.abs(cb - ca) + 1) * (Math.abs(rb - ra) + 1) > 10000)
                throw new FormulaError('범위가 너무 큽니다');
            for (let r = Math.min(ra, rb); r <= Math.max(ra, rb); r++)
                for (let c = Math.min(ca, cb); c <= Math.max(ca, cb); c++)
                    out.push(colName(c) + r);
            return out.join(',');
        })
            .replace(/\bAVERAGE\s*\(/gi, 'AVG(').replace(/\bCOUNTA\s*\(/gi, 'COUNT(').replace(/\bROUNDDOWN\s*\(/gi, 'TRUNC(').replace(/\bINT\s*\(/gi, 'FLOOR(');
    }).join('');
    const ds = { get: (_r, id) => { const v = get(id.toUpperCase()); return v === undefined ? null : v; } };
    const v = compileFormula(src)({ ds: ds, row: 0 });
    return typeof v === 'number' && !isFinite(v) ? null : v;
}
/** 통합 문서 구조: 시트 이름 → 시트 파일 경로 */
async function workbookSheets(buf) {
    const files = await unzip(buf, n => n === 'xl/workbook.xml' || n === 'xl/_rels/workbook.xml.rels');
    const dec = new TextDecoder();
    const wb = files.get('xl/workbook.xml'), rels = files.get('xl/_rels/workbook.xml.rels');
    const out = [];
    if (wb) {
        const rid = new Map();
        if (rels)
            for (const m of dec.decode(rels).matchAll(/<Relationship\b([^>]*)\/?>/g)) {
                const id = attr(m[1], 'Id'), t = attr(m[1], 'Target');
                if (!id || !t)
                    continue;
                rid.set(id, t.startsWith('/') ? t.slice(1) : 'xl/' + t.replace(/^\.\//, ''));
            }
        for (const m of dec.decode(wb).matchAll(/<sheet\b([^>]*)\/?>/g)) {
            const name = unxml(attr(m[1], 'name') ?? ''), id = /(?:^|\s)[A-Za-z]+:id="([^"]*)"/.exec(m[1])?.[1];
            const path = (id && rid.get(id)) || `xl/worksheets/sheet${out.length + 1}.xml`;
            out.push({ name, path });
        }
    }
    return out;
}
const toBuf = async (file) => file instanceof Uint8Array ? file : new Uint8Array(file instanceof ArrayBuffer ? file : await file.arrayBuffer());
/** .xlsx 의 시트 이름 목록 */
export async function listXlsxSheets(file) {
    return (await workbookSheets(await toBuf(file))).map(s => s.name);
}
/**
 * .xlsx 시트 하나를 읽어 첫 줄을 headers, 나머지를 rows 로 (값: 문자열·숫자·논리).
 * opts.sheet 로 시트 선택(이름·번호), 날짜 서식 셀은 날짜 글자로, 수식은 formulas 로 함께 돌려줌
 */
export async function importXlsx(file, opts = {}) {
    const buf = await toBuf(file);
    const list = await workbookSheets(buf);
    let pick;
    if (opts.sheet == null)
        pick = list[0];
    else if (typeof opts.sheet === 'number')
        pick = list[opts.sheet];
    else
        pick = list.find(s => s.name === opts.sheet) ?? list.find(s => s.name.toLowerCase() === String(opts.sheet).toLowerCase());
    if (opts.sheet != null && !pick)
        throw new Error(`시트가 없습니다: ${opts.sheet} (있는 시트: ${list.map(s => s.name).join(', ')})`);
    const files = await unzip(buf, n => n === 'xl/sharedStrings.xml' || n === 'xl/styles.xml' || (pick ? n === pick.path : /^xl\/worksheets\/[^/]+\.xml$/.test(n)));
    const dec = new TextDecoder();
    const shared = [];
    const ss = files.get('xl/sharedStrings.xml');
    if (ss)
        for (const m of dec.decode(ss).matchAll(/<si>([\s\S]*?)<\/si>/g))
            shared.push(texts(m[1]));
    const sheetPath = pick?.path ?? [...files.keys()].filter(k => k.startsWith('xl/worksheets/')).sort((a, b) => parseInt(a.replace(/\D/g, '')) - parseInt(b.replace(/\D/g, '')))[0];
    if (!sheetPath || !files.get(sheetPath))
        throw new Error('시트가 없습니다');
    // 셀 서식 번호 → 날짜 종류
    const xfKind = [];
    const st = files.get('xl/styles.xml');
    if (st && opts.dates !== false) {
        const sx = dec.decode(st), codes = new Map();
        for (const m of sx.matchAll(/<numFmt\b([^>]*)\/?>/g)) {
            const id = attr(m[1], 'numFmtId'), c = attr(m[1], 'formatCode');
            if (id != null && c != null)
                codes.set(+id, unxml(c));
        }
        const cx = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(sx)?.[1] ?? '';
        for (const m of cx.matchAll(/<xf\b([^>]*?)(?:\/>|>)/g)) {
            const id = +(attr(m[1], 'numFmtId') ?? 0), code = codes.get(id);
            const kind = dateFormatKind(code, codes.has(id) ? undefined : id);
            xfKind.push(kind ? { kind, sec: code ? /s/i.test(code.replace(/"[^"]*"/g, '')) : id === 19 || id === 21 || id === 46 || id === 47 } : null);
        }
    }
    const xml = dec.decode(files.get(sheetPath));
    const grid = [];
    const rowNo = [];
    const formulas = {};
    const sharedF = new Map();
    const pending = [];
    for (const rm of xml.matchAll(/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g)) {
        const rn = /\br="(\d+)"/.exec(rm[1]);
        const ri = rn ? +rn[1] - 1 : grid.length;
        const row = [];
        for (const cm of (rm[2] ?? '').matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
            const attrs = cm[1], body = cm[2] ?? '';
            const ref = /\br="([A-Z]+\d*)"/.exec(attrs)?.[1];
            const ci = ref ? colIndex(ref) : row.length;
            const t = /\bt="(\w+)"/.exec(attrs)?.[1];
            const s = /\bs="(\d+)"/.exec(attrs)?.[1];
            const v = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1];
            const dk = s != null ? xfKind[+s] ?? null : null;
            let val = null;
            if (t === 's')
                val = v != null ? shared[+v] ?? null : null;
            else if (t === 'inlineStr')
                val = texts(body);
            else if (t === 'b')
                val = v === '1';
            else if (t === 'str' || t === 'e')
                val = v != null ? unxml(v) : null;
            else if (v != null && v !== '') {
                val = Number(v);
                if (dk && isFinite(val))
                    val = excelDateTime(val, dk.kind, dk.sec);
            }
            // 수식: <f>식</f> 또는 공유 수식 <f t="shared" si="0" ref="C2:C9">식</f> / <f t="shared" si="0"/>
            const fm = /<f\b([^>]*?)(?:\/>|>([\s\S]*?)<\/f>)/.exec(body);
            if (fm) {
                const cellRef = ref ?? colName(ci) + (ri + 1);
                let f = fm[2] != null ? unxml(fm[2]) : '';
                const si = attr(fm[1], 'si');
                if (attr(fm[1], 't') === 'shared' && si != null) {
                    if (f)
                        sharedF.set(si, { f, r: ri, c: ci });
                    else {
                        const base = sharedF.get(si);
                        if (base)
                            f = shiftFormula(base.f, ri - base.r, ci - base.c);
                    }
                }
                if (f) {
                    formulas[cellRef] = f;
                    if (opts.evalFormulas && (v == null || v === ''))
                        pending.push({ ri, ci, ref: cellRef, f, dk });
                }
            }
            row[ci] = val;
        }
        for (let i = 0; i < row.length; i++)
            if (row[i] === undefined)
                row[i] = null;
        grid[ri] = row;
        rowNo[ri] = ri + 1;
    }
    // 계산 값 없는 수식 셀 계산 (서로 참조하면 필요한 만큼 먼저 계산, 순환은 null)
    if (pending.length) {
        const pend = new Map(pending.map(p => [p.ref, p]));
        const busy = new Set();
        const get = (ref) => {
            var _a, _b;
            const p = pend.get(ref);
            if (p) {
                if (busy.has(ref))
                    throw new FormulaError('순환 참조');
                busy.add(ref);
                try {
                    let r = evalExcelFormula(p.f, get);
                    if (typeof r === 'number' && p.dk)
                        r = excelDateTime(r, p.dk.kind, p.dk.sec);
                    (grid[_a = p.ri] ?? (grid[_a] = []))[p.ci] = r ?? null;
                }
                catch {
                    (grid[_b = p.ri] ?? (grid[_b] = []))[p.ci] = null;
                }
                busy.delete(ref);
                pend.delete(ref);
            }
            const m = /^([A-Z]+)(\d+)$/.exec(ref);
            if (!m)
                return null;
            const v = grid[+m[2] - 1]?.[colIndex(m[1])];
            return v === undefined ? null : v;
        };
        for (const p of pending)
            if (pend.has(p.ref))
                get(p.ref);
    }
    const all = Array.from(grid, (r, i) => ({ r: (r ?? []).map(x => (x === undefined ? null : x)), n: rowNo[i] ?? i + 1 }));
    const head = all.shift();
    const headers = (head?.r ?? []).map(h => (h == null ? '' : String(h).trim()));
    const kept = all.filter(x => x.r.some(v => v != null && v !== ''));
    return { headers, rows: kept.map(x => x.r), sheet: pick?.name, sheets: list.map(s => s.name), rowNums: kept.map(x => x.n), formulas };
}
